import { LogLevel } from '@datocms/cma-client-browser';
import Modal from '@src/components/common/Modal';
import {
  type ControlConfiguration,
  QuoteControlType,
} from '@src/components/sidepanel/CompositionSettings/types';
import { createDatoClient } from '@src/utility/dato';
import {
  Button,
  CaretDownIcon,
  CaretUpIcon,
  Dropdown,
  DropdownMenu,
  DropdownOption,
} from 'datocms-react-ui';
import React, { useMemo, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { RenderFieldExtensionCtx } from 'datocms-plugin-sdk';

type ImageLogoControlSelectorProps = {
  ctx: RenderFieldExtensionCtx;
  control: ControlConfiguration & { enabled?: boolean };
  onUpdateControlType: (value: 'image' | 'logo' | 'headshot') => void;
  onUpdateCustomValue: (
    newValue:
      | {
          value?: string | number;
          quote?: QuoteControlType;
        }
      | undefined,
  ) => void;
  imageOptions: { id: string; label: string; url: string }[];
  logoOptions: { id: string; label: string; url: string }[];
  mediaRefetch: () => void;
};

export function ImageLogoControlSelector({
  ctx,
  control,
  onUpdateControlType,
  onUpdateCustomValue,
  imageOptions,
  logoOptions,
  mediaRefetch,
}: ImageLogoControlSelectorProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [openCustomValueModal, setOpenCustomValueModal] = useState(false);
  const [modalLoader, setModalLoader] = useState(false);

  const selectedOption =
    control.type === 'image'
      ? imageOptions.find(option => option.url === control.default?.value)
      : logoOptions.find(option => option.url === control.default?.value);

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setModalLoader(true);

    const dClient = createDatoClient({
      apiToken: ctx.currentUserAccessToken || '',
      environment: ctx.environment,
      logLevel: LogLevel.NONE,
    });
    const file = e.target.files?.[0];
    if (!file) {
      setModalLoader(false);
      return;
    }
    const upload = await dClient.uploads.createFromFileOrBlob({
      fileOrBlob: file,
      filename: file.name,
    });
    if (upload) {
      onUpdateCustomValue({ value: upload.url });
      setOpenCustomValueModal(false);
      setModalLoader(false);
    }
    const showcaseId = ctx.item?.attributes.created_by_showcase as string;
    if (!showcaseId) return;
    const showcase = (await dClient.items.find(
      showcaseId as string,
    )) as unknown as {
      organization_artifacts: { upload_id: string; title: string }[];
      organization_logos: { upload_id: string; title: string }[];
    };

    if (!showcase) return;
    try {
      if (control.type === 'image') {
        await dClient.items.update(showcaseId as string, {
          organization_artifacts: [
            ...(showcase.organization_artifacts || []),
            { upload_id: upload.id, title: file.name },
          ],
        });
      } else {
        await dClient.items.update(showcaseId as string, {
          organization_logos: [
            ...(showcase.organization_logos || []),
            { upload_id: upload.id, title: file.name },
          ],
        });
      }
      mediaRefetch();
    } catch (err) {
      setModalLoader(false);
      setOpenCustomValueModal(false);
      alert(
        'Failed to update item with uploaded image/logo. Please try again.',
      );
      return;
    }
  };

  const previewImageUrl = useMemo(() => {
    if (!control.default?.value) {
      return;
    }
    try {
      new URL(control.default?.value as string);
      return control.default?.value as string;
    } catch {
      return undefined;
    }
  }, [control.default?.value]);

  return (
    <>
      <input
        type="file"
        ref={inputRef}
        accept="image/*"
        onChange={handleUploadImage}
        style={{ display: 'none' }}
      />
      <Dropdown
        renderTrigger={({ open, onClick }) => (
          <Button
            buttonSize="xxs"
            type="button"
            onClick={onClick}
            rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
          >
            <span style={{ textTransform: 'capitalize' }}>
              {control.type === 'image' &&
              control.default?.quote === QuoteControlType.BACKGROUND
                ? 'Quote Background'
                : control.type}
            </span>
          </Button>
        )}
      >
        <DropdownMenu>
          <DropdownOption
            active={
              control.type === 'image' &&
              control.default?.quote !== QuoteControlType.BACKGROUND
            }
            onClick={() => {
              onUpdateControlType('image');
              onUpdateCustomValue(undefined);
            }}
          >
            Image
          </DropdownOption>
          <DropdownOption
            active={control.type === 'logo'}
            onClick={() => {
              onUpdateControlType('logo');
            }}
          >
            Logo
          </DropdownOption>
          <DropdownOption
            active={
              control.type === 'image' &&
              control.default?.quote === QuoteControlType.BACKGROUND
            }
            onClick={() => {
              onUpdateControlType('image');
              onUpdateCustomValue({ quote: QuoteControlType.BACKGROUND });
            }}
          >
            Quote Background
          </DropdownOption>
        </DropdownMenu>
      </Dropdown>
      {control.default?.quote !== QuoteControlType.BACKGROUND && (
        <Dropdown
          renderTrigger={({ open, onClick }) => (
            <Button
              buttonSize="xxs"
              type="button"
              onClick={onClick}
              rightIcon={open ? <CaretUpIcon /> : <CaretDownIcon />}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  maxWidth: '200px',
                }}
              >
                <span
                  style={{
                    maxWidth: '160px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {' '}
                  {selectedOption
                    ? selectedOption.label
                    : control.default?.value
                      ? `Selected ${control.type}`
                      : control.type === 'image'
                        ? 'Select Image'
                        : 'Select Logo'}
                </span>
                {previewImageUrl && (
                  <img
                    src={previewImageUrl}
                    alt={selectedOption?.label || 'Preview'}
                    style={{
                      maxWidth: '30px',
                      maxHeight: '30px',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </div>
            </Button>
          )}
        >
          <DropdownMenu>
            <DropdownOption
              active={!!control.default?.value}
              onClick={() => {
                setOpenCustomValueModal(true);
              }}
            >
              Select
            </DropdownOption>
            <DropdownOption
              active={!control.default?.value}
              onClick={() => {
                onUpdateCustomValue(undefined);
              }}
            >
              None
            </DropdownOption>
          </DropdownMenu>
        </Dropdown>
      )}
      {openCustomValueModal && (
        <ChooseImageModal
          open={openCustomValueModal}
          onClose={() => setOpenCustomValueModal(false)}
          options={control.type === 'image' ? imageOptions : logoOptions}
          onUpdateCustomValue={value => {
            onUpdateCustomValue({ value: value });
            setOpenCustomValueModal(false);
          }}
          onUploadImage={() => {
            inputRef.current?.click();
          }}
          modalLoader={modalLoader}
        />
      )}
    </>
  );
}

function ChooseImageModal({
  open,
  onClose,
  options,
  onUpdateCustomValue,
  onUploadImage,
  modalLoader,
}: {
  open: boolean;
  onClose: () => void;
  options: { id: string; label: string; url: string }[];
  onUpdateCustomValue: (value: string) => void;
  onUploadImage: () => void;
  modalLoader: boolean;
}) {
  return (
    <Modal isOpen={open} closeModal={onClose} crossColor="#333333">
      <Wrapper>
        <ImageWrapper>
          {options.map(option => (
            <>
              <ImageFrame
                key={option.id}
                onClick={() => {
                  onUpdateCustomValue(option.url);
                }}
                data-tooltip-id={`image-description-tooltip-${option.id}`}
                data-tooltip-content={option.label}
              >
                <img
                  src={option.url}
                  alt={option.label}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              </ImageFrame>
              <Tooltip
                id={`image-description-tooltip-${option.id}`}
                place="top-start"
                style={{
                  padding: '8px',
                  fontSize: '10px',
                  lineHeight: '140%',
                  zIndex: '10',
                }}
              />
            </>
          ))}
        </ImageWrapper>
        <ModalButton
          buttonSize="xxs"
          onClick={onUploadImage}
          disabled={modalLoader}
        >
          {modalLoader ? 'Uploading...' : 'Upload Image'}
        </ModalButton>
      </Wrapper>
    </Modal>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 24px;
  width: 600px;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #f1f1f1;
  box-sizing: border-box;
  background: #fefefe;
`;

const ImageWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 16px;
  max-height: 500px;
  overflow-y: auto;
  scrollbar-color: #cccccc #f5f5f5 !important;
`;

const ImageFrame = styled.div`
  max-width: 150px;
  max-height: 100px;
  border-radius: 8px;
  box-sizing: border-box;
  cursor: pointer;
`;

const ModalButton = styled(Button)`
  width: 100%;
  position: sticky;
  bottom: 0;
  background-color: rgb(23, 121, 212);
  color: #fff;
  font-weight: 500;
  font-size: 16.5px;
  padding: 12px;
  border-radius: 4px;
`;
